// screen: Library list (table/grid)
import * as React from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useLingui, Trans } from '@lingui/react/macro';
import { LayoutGrid, List, Trash2, Clapperboard, Upload, Eye, Loader2 } from 'lucide-react';
import type { AssetSummary } from '@viewstream/api-client';
import { ApiError } from '@viewstream/api-client';
import { Badge, Button, DataTable, type ColumnDef, Dialog, DialogContent, EmptyState, ErrorState, Select, Skeleton, cn, useToast } from '@viewstream/ui';
import { Player } from '@viewstream/player-react';
import { fmtDateTime, fmtDuration } from '@viewstream/i18n';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';
import { can } from '~/app/permissions';
import { useUploads } from '~/app/upload/queue';
import { SectionsSidebar } from '~/app/Sections';
import { Highlight, MatchHint, SearchBox } from '~/app/SearchHint';
import { SitemapImportButtons } from '~/app/libraryImport/SitemapImport';
import { sectionTree, useSectionMutations, useSections } from '~/app/presets';
import { ASSET_STATUSES, AssetStatusBadge, masterUrl, posterUrl, useAssetPages, useAssetStatusLabel, useDelete, usePublish, type AssetFilters } from '~/app/assets';
import { VideoId } from '~/app/VideoId';

export const Route = createFileRoute('/_layout/t/$slug/library')({ component: Screen });

type View = 'table' | 'grid';
const VIEW_KEY = 'vs.library.view'; // UI preference only

function Screen() {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const { locale, tz } = useLocale();
  const navigate = useNavigate();
  const toast = useToast();
  const uploads = useUploads();
  const statusLabel = useAssetStatusLabel();
  React.useEffect(() => { document.title = appTitle(t`Library`); }, [t]);

  const [filters, setFilters] = React.useState<AssetFilters>({});
  const [view, setView] = React.useState<View>(() => { try { return (localStorage.getItem(VIEW_KEY) as View) || 'table'; } catch { return 'table'; } });
  const narrow = useNarrow();
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [preview, setPreview] = React.useState<AssetSummary | null>(null);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const pages = useAssetPages(tenant.slug, filters);
  const publish = usePublish(tenant.slug);
  const del = useDelete(tenant.slug);
  const canWrite = can(scopes, 'assets:write');
  const canPublish = can(scopes, 'assets:publish');
  const sections = useSections(tenant.slug);
  const secM = useSectionMutations(tenant.slug);
  const secOptions = sectionTree(sections.data ?? []).flatMap((s) => [{ value: s.id, label: s.name }, ...s.children.map((c) => ({ value: c.id, label: `${s.name} › ${c.name}` }))]);
  const activeSection = sections.data?.find((s) => s.id === filters.collection);
  const sectionOp = (op: 'add' | 'move' | 'remove', target?: string) => {
    const id = op === 'add' ? target! : filters.collection!;
    secM.items.mutate({ op, id, asset_ids: [...selected], to: op === 'move' ? target : undefined }, {
      onSuccess: () => { toast.push({ title: op === 'remove' ? t`Removed from the section` : op === 'move' ? t`Moved to the section` : t`Added to the section`, tone: 'ok' }); setSelected(new Set()); },
      onError: (e) => toast.push({ title: t`Could not change the section`, description: e instanceof ApiError ? e.problem?.detail : String(e), tone: 'danger' }),
    });
  };

  const setViewPersist = (v: View) => { setView(v); try { localStorage.setItem(VIEW_KEY, v); } catch { /* private mode */ } };
  const open = (a: AssetSummary, tab?: string) => void navigate({ to: '/t/$slug/library/$assetId', params: { slug: tenant.slug, assetId: a.id! }, search: tab ? { tab } : {} } as never);
  const togglePublish = (a: AssetSummary) => {
    if (!canPublish) { toast.push({ title: t`You cannot publish`, description: t`Publishing needs the Publisher role.`, tone: 'danger' }); return; }
    const next = !a.published_at;
    publish.mutate({ ids: [a.id!], publish: next }, {
      onSuccess: () => toast.push({ title: next ? t`Published` : t`Unpublished`, description: a.title ?? a.id, tone: 'ok' }),
      onError: (e) => toast.push({ title: t`Could not change publishing`, description: e instanceof ApiError ? e.problem?.detail : String(e), tone: 'danger' }),
    });
  };
  // lazy loading: the next page (50) loads as the end of the list nears the viewport; not for a one-character filter
  // (it only narrows the rows already loaded — auto-loading would pull the whole library to fill the screen)
  const autoLoad = !pages.clientFiltered;
  const loadMore = React.useCallback(() => { if (pages.hasNextPage && !pages.isFetchingNextPage) void pages.fetchNextPage({ cancelRefetch: false }); },
    [pages.hasNextPage, pages.isFetchingNextPage, pages.fetchNextPage]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleSel = (id: string) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const columns = React.useMemo<ColumnDef<AssetSummary, unknown>[]>(() => [
    {
      id: 'select', header: () => <span className="sr-only">{t`Select`}</span>, size: 40, meta: { width: '44px' },
      cell: ({ row }) => <input type="checkbox" aria-label={t`Select ${row.original.title ?? row.original.id}`} checked={selected.has(row.original.id!)} onChange={() => toggleSel(row.original.id!)} onClick={(e) => e.stopPropagation()} />,
    },
    { id: 'poster', meta: { width: '104px' }, header: () => <span className="sr-only">{t`Poster`}</span>, cell: ({ row }) => <Player mode="tile" src={masterUrl(tenant, row.original)} poster={posterUrl(tenant, row.original)} posterLoading="lazy" label={t`Preview of ${row.original.title ?? ''}`} className="w-20" /> },
    { id: 'title', meta: { width: 'minmax(200px, 2.6fr)' }, header: t`Title`, cell: ({ row }) => (
      <div className="min-w-0">
        <Link to={'/t/$slug/library/$assetId' as never} params={{ slug: tenant.slug, assetId: row.original.id } as never} className="font-medium hover:underline">
          {row.original.title ? <Highlight text={row.original.title} q={pages.term} /> : t`(untitled)`}
        </Link>
        <MatchHint match={row.original.match} q={pages.term} className="line-clamp-1" />
        <VideoId id={row.original.id} className="mt-0.5 flex w-fit max-w-full" />
      </div>
    ) },
    { id: 'status', meta: { width: 'minmax(120px, 1fr)' }, header: t`Status`, cell: ({ row }) => <AssetStatusBadge status={row.original.status} /> },
    { id: 'published', meta: { width: 'minmax(110px, 1fr)' }, header: t`Published`, cell: ({ row }) => (row.original.published_at ? <Badge tone="ok">{t`Published`}</Badge> : <Badge>{t`Draft`}</Badge>) },
    { id: 'duration', meta: { width: 'minmax(80px, 0.7fr)' }, header: t`Duration`, cell: ({ row }) => <span className="ltr-isolate">{row.original.duration_ms ? fmtDuration(row.original.duration_ms) : '—'}</span> },
    { id: 'created', meta: { width: 'minmax(200px, 1.3fr)' }, header: t`Created`, cell: ({ row }) => fmtDateTime(row.original.created_at!, { locale, tz }) },
  ], [t, selected, tenant, locale, tz, pages.term]);

  const onRowKey = (a: AssetSummary, e: React.KeyboardEvent) => {
    if (e.key === ' ') { e.preventDefault(); setPreview(a); }
    else if (e.key.toLowerCase() === 'p') { e.preventDefault(); togglePublish(a); }
    else if (e.key.toLowerCase() === 'e') { e.preventDefault(); open(a, 'metadata'); }
  };

  const bulkIds = [...selected];
  const statusOptions = [{ value: '__all', label: t`All statuses` }, ...ASSET_STATUSES.map((s) => ({ value: s, label: statusLabel(s) }))];

  return (
    <div className="grid gap-4 lg:grid-cols-[14rem_minmax(0,1fr)]">
      <SectionsSidebar tenant={tenant.slug} active={filters.collection} onSelect={(id) => { setSelected(new Set()); setFilters((f) => ({ ...f, collection: id })); }} canWrite={canWrite} />
      <div className="min-w-0">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">{t`Library`}{activeSection ? <span className="text-fg-muted"> › <bdi>{activeSection.name}</bdi></span> : null}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Link to={'/t/$slug/series' as never} params={{ slug: tenant.slug } as never} className="inline-flex h-10 items-center gap-2 rounded-md px-4 text-sm hover:bg-surface-2"><Clapperboard className="size-4" aria-hidden /> {t`Series`}</Link>
          <Link to={'/t/$slug/library/trash' as never} params={{ slug: tenant.slug } as never} className="inline-flex h-10 items-center gap-2 rounded-md px-4 text-sm hover:bg-surface-2"><Trash2 className="size-4" aria-hidden /> {t`Trash`}</Link>
          <SitemapImportButtons tenant={tenant.slug} canWrite={canWrite} />
          {canWrite ? <Button onClick={() => uploads.setOpen(true)}><Upload className="size-4" aria-hidden /> {t`Upload`}</Button> : null}
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        {/* server-side search: title, external id, description, summary, presenters, topics (2+ characters) */}
        <SearchBox label={t`Search titles, descriptions, summaries, presenters`} className="w-full sm:w-80" value={filters.q ?? ''}
          onChange={(v) => setFilters((f) => ({ ...f, q: v }))} testId="library-search" />
        <Select ariaLabel={t`Status`} value={filters.status ?? '__all'} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === '__all' ? undefined : (v as AssetFilters['status']) }))} options={statusOptions} className="w-40" />
        <Select ariaLabel={t`Publishing`} value={filters.published ?? '__all'} onValueChange={(v) => setFilters((f) => ({ ...f, published: v === '__all' ? undefined : (v as 'yes' | 'no') }))}
          options={[{ value: '__all', label: t`Published and drafts` }, { value: 'yes', label: t`Published` }, { value: 'no', label: t`Drafts` }]} className="w-56" />
        <div className="ms-auto flex rounded-md border border-border" role="group" aria-label={t`View`}>
          <Button variant={view === 'table' ? 'secondary' : 'ghost'} size="icon" aria-pressed={view === 'table'} aria-label={t`Table view`} onClick={() => setViewPersist('table')}><List className="size-4" aria-hidden /></Button>
          <Button variant={view === 'grid' ? 'secondary' : 'ghost'} size="icon" aria-pressed={view === 'grid'} aria-label={t`Grid view`} onClick={() => setViewPersist('grid')}><LayoutGrid className="size-4" aria-hidden /></Button>
        </div>
      </div>

      {bulkIds.length ? (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface-2 p-2 text-sm" role="region" aria-label={t`Bulk actions`}>
          <span className="px-2"><Trans>{bulkIds.length} selected</Trans></span>
          {canPublish ? <Button size="sm" variant="secondary" loading={publish.isPending} onClick={() => publish.mutate({ ids: bulkIds, publish: true }, { onSuccess: () => { toast.push({ title: t`Published`, tone: 'ok' }); setSelected(new Set()); } })}>{t`Publish`}</Button> : null}
          {canPublish ? <Button size="sm" variant="secondary" loading={publish.isPending} onClick={() => publish.mutate({ ids: bulkIds, publish: false }, { onSuccess: () => { toast.push({ title: t`Unpublished`, tone: 'ok' }); setSelected(new Set()); } })}>{t`Unpublish`}</Button> : null}
          {canWrite && secOptions.length ? (
            <Select ariaLabel={t`Add to section`} placeholder={t`Add to section`} value="" onValueChange={(v) => sectionOp('add', v)} options={secOptions.filter((o) => o.value !== filters.collection)} className="h-9 min-w-44" />
          ) : null}
          {canWrite && filters.collection && secOptions.length > 1 ? (
            <Select ariaLabel={t`Move to section`} placeholder={t`Move to section`} value="" onValueChange={(v) => sectionOp('move', v)} options={secOptions.filter((o) => o.value !== filters.collection)} className="h-9 min-w-44" />
          ) : null}
          {canWrite && filters.collection ? <Button size="sm" variant="secondary" loading={secM.items.isPending} onClick={() => sectionOp('remove')}>{t`Remove from section`}</Button> : null}
          {canWrite ? <Button size="sm" variant="danger" onClick={() => setConfirmDelete(true)}><Trash2 className="size-4" aria-hidden /> {t`Move to trash`}</Button> : null}
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>{t`Clear selection`}</Button>
        </div>
      ) : null}

      {pages.isPending ? (
        <div className="space-y-2">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-11 w-full" />)}</div>
      ) : pages.isError ? (
        <ErrorState error={pages.error as ApiError} onRetry={() => pages.refetch()} labels={{ title: t`Could not load the library`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />
      ) : !pages.rows.length ? (
        <EmptyState icon={<Upload className="size-6" aria-hidden />} title={filters.q || filters.status || filters.published || filters.collection ? t`Nothing matches these filters` : t`No assets yet`}
          description={filters.q || filters.status || filters.published || filters.collection ? t`Clear the filters or search for something else.` : t`Upload a video or import from S3 or a URL list to start your library.`}
          actions={canWrite && !(filters.q || filters.status || filters.published || filters.collection) ? <Button onClick={() => uploads.setOpen(true)}><Upload className="size-4" aria-hidden /> {t`Upload`}</Button> : undefined} />
      ) : view === 'table' && !narrow ? (
        <>
          <DataTable caption={t`Assets`} columns={columns} rows={pages.rows} getRowId={(a) => a.id!} onRowActivate={(a) => open(a)} onRowKeyDown={onRowKey}
            rowProps={(a) => ({ 'data-asset-id': a.id, 'aria-selected': selected.has(a.id!) })}
            onEndReached={autoLoad && !pages.isFetchNextPageError ? loadMore : undefined} rowHeight={64} windowScroll />
          <ListEnd pages={pages} auto={autoLoad} onMore={loadMore} />
          <p className="mt-2 text-xs text-fg-muted">{t`Keyboard: Enter opens · Space previews · P publishes or unpublishes · E edits metadata`}</p>
        </>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" aria-label={t`Assets`}>
          {pages.rows.map((a) => (
            <li key={a.id} data-asset-id={a.id} className={cn('rounded-lg border border-border bg-surface p-2', selected.has(a.id!) && 'ring-2 ring-primary')}>
              <button type="button" className="block w-full text-start" onClick={() => open(a)} onKeyDown={(e) => onRowKey(a, e)}>
                <Player mode="tile" src={masterUrl(tenant, a)} poster={posterUrl(tenant, a)} posterLoading="lazy" label={t`Preview of ${a.title ?? ''}`} />
                <span className="mt-2 block truncate font-medium">{a.title ? <Highlight text={a.title} q={pages.term} /> : t`(untitled)`}</span>
              </button>
              <MatchHint match={a.match} q={pages.term} className="mt-1 line-clamp-3" />
              <VideoId id={a.id} className="mt-1 max-w-full" />
              <div className="mt-1 flex items-center justify-between gap-2">
                <AssetStatusBadge status={a.status} />
                <label className="flex items-center gap-1 text-xs text-fg-muted">
                  <input type="checkbox" checked={selected.has(a.id!)} onChange={() => toggleSel(a.id!)} /> {t`Select`}
                </label>
              </div>
            </li>
          ))}
          <li className="col-span-full"><ListEnd pages={pages} auto={autoLoad} onMore={loadMore} /></li>
        </ul>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        {preview ? (
          <DialogContent title={preview.title || t`(untitled)`} closeLabel={t`Close`}>
            <Player mode="main" poster={posterUrl(tenant, preview)} label={t`Preview of ${preview.title ?? ''}`} />
            <div className="mt-3 flex justify-end"><Button variant="secondary" onClick={() => { setPreview(null); open(preview); }}><Eye className="size-4" aria-hidden /> {t`Open`}</Button></div>
          </DialogContent>
        ) : null}
      </Dialog>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent title={t`Move ${bulkIds.length} assets to trash?`} description={t`Their renditions are purged from the CDN library. Masters are kept.`} closeLabel={t`Close`}>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>{t`Cancel`}</Button>
            <Button variant="danger" loading={del.isPending} onClick={() => del.mutate(bulkIds, {
              onSuccess: () => { toast.push({ title: t`Moved to trash`, tone: 'ok' }); setSelected(new Set()); setConfirmDelete(false); },
              onError: (e) => toast.push({ title: t`Could not delete`, description: e instanceof ApiError ? e.problem?.detail : String(e), tone: 'danger' }),
            })}>{t`Move to trash`}</Button>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}

/**
 * End of the Library list (table and grid): an IntersectionObserver sentinel that loads the next page when it comes
 * within ~800 px of the viewport, a small loading row while it loads, "Load more" as the fallback (keyboard, a
 * one-character filter, a failed page), and the count once everything is loaded.
 */
function ListEnd({ pages, auto, onMore }: {
  pages: { rows: unknown[]; hasNextPage: boolean; isFetchingNextPage: boolean; isFetchNextPageError: boolean }; auto: boolean; onMore: () => void;
}) {
  const { t } = useLingui();
  const ref = React.useRef<HTMLDivElement>(null);
  const [near, setNear] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([e]) => setNear(!!e?.isIntersecting), { rootMargin: '0px 0px 800px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  // still near after a page landed (a short page, a tall screen): keep going; a failed page waits for the button
  React.useEffect(() => {
    if (auto && near && pages.hasNextPage && !pages.isFetchingNextPage && !pages.isFetchNextPageError) onMore();
  }, [auto, near, pages.hasNextPage, pages.isFetchingNextPage, pages.isFetchNextPageError, pages.rows.length, onMore]);
  const n = pages.rows.length;
  return (
    <div ref={ref} className="flex min-h-10 items-center justify-center gap-2 py-2 text-xs text-fg-muted" data-testid="library-list-end">
      {pages.isFetchingNextPage ? (
        <span role="status" className="inline-flex items-center gap-2"><Loader2 className="size-4 animate-spin" aria-hidden /> {t`Loading more…`}</span>
      ) : pages.hasNextPage ? (
        <Button size="sm" variant="ghost" onClick={onMore}>{pages.isFetchNextPageError ? t`Could not load more — retry` : t`Load more`}</Button>
      ) : n ? (
        <span><Trans>All {n} shown</Trans></span>
      ) : null}
    </div>
  );
}

/** Below 640 px the table would need horizontal scrolling: the Library shows the card grid instead. */
function useNarrow(): boolean {
  const q = '(max-width: 639px)';
  const [n, setN] = React.useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
  React.useEffect(() => {
    const m = window.matchMedia(q); const on = () => setN(m.matches);
    m.addEventListener('change', on); return () => m.removeEventListener('change', on);
  }, []);
  return n;
}
